import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { api, errMsg, canVoteEmail, PLACES } from "./lib/api";
import { Hero } from "./components/Hero";
import { Tabs } from "./components/Tabs";
import { IdentityBar } from "./components/IdentityBar";
import { Gallery } from "./components/Gallery";
import { Ballot } from "./components/Ballot";
import { UploadForm } from "./components/UploadForm";
import { Results } from "./components/Results";
import { Admin } from "./components/Admin";
import { Lightbox } from "./components/Lightbox";

const TABS = ["galeria", "participar", "resultados", "admin"];
const EMPTY = { first: null, second: null, third: null };
const readTab = () => { const t = window.location.hash.slice(1); return TABS.includes(t) ? t : "galeria"; };

export default function App() {
  const [email, setEmail] = useState(() => localStorage.getItem("ptl_email") || "");
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState(readTab);
  const [ballot, setBallot] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lb, setLb] = useState(null);

  const refresh = useCallback(async () => {
    try { setData(await api.state(email)); setErr(""); }
    catch (_) { setErr("No se pudo conectar todavía. Revisa tu conexión; la página seguirá intentando."); }
    finally { setLoaded(true); }
  }, [email]);

  useEffect(() => { refresh(); const t = setInterval(refresh, 10000); return () => clearInterval(t); }, [refresh]);
  useEffect(() => { const h = () => setTab(readTab()); window.addEventListener("hashchange", h); return () => window.removeEventListener("hashchange", h); }, []);
  useEffect(() => { if (!dirty && !saving) setBallot(data?.myVote || EMPTY); }, [data, dirty, saving]);
  useEffect(() => { setDirty(false); }, [email]);

  const setIdentity = (e) => { localStorage.setItem("ptl_email", e); setEmail(e); };
  const clearIdentity = () => { localStorage.removeItem("ptl_email"); setEmail(""); setData(null); };

  const entries = data?.entries || [];
  const canVote = canVoteEmail(email) && !!data?.config.votingOpen;
  const activeTab = tab === "admin" && !data?.isAdmin ? "galeria" : tab;

  const pick = (place, id) => {
    setBallot((b) => {
      const was = b[place] === id, n = { ...b };
      PLACES.forEach((p) => { if (n[p] === id) n[p] = null; });
      if (!was) n[place] = id;
      return n;
    });
    setDirty(true);
  };

  const saveVote = async () => {
    setSaving(true);
    try { await api.saveVote({ email, ...ballot }); setDirty(false); toast("¡Votos guardados! Puedes cambiarlos cuando quieras."); await refresh(); }
    catch (e) { toast(errMsg(e)); } finally { setSaving(false); }
  };

  const showBallot = activeTab === "galeria" && canVote && entries.some((e) => !e.mine);

  return (
    <div className="app">
      <Hero config={data?.config} />
      <Tabs tab={activeTab} isAdmin={data?.isAdmin} />
      <main className="wrap">
        {err && <div className="banner err" data-testid="status-banner">{err}</div>}
        <IdentityBar email={email} onSet={setIdentity} onClear={clearIdentity} />
        {activeTab === "galeria" && <Gallery data={data} loaded={loaded} ballot={ballot} canVote={canVote} email={email} onPick={pick} onOpen={setLb} />}
        {activeTab === "participar" && <UploadForm data={data} email={email} onDone={() => { refresh(); window.location.hash = "galeria"; }} />}
        {activeTab === "resultados" && <Results data={data} onOpen={setLb} />}
        {activeTab === "admin" && data?.isAdmin && <Admin data={data} email={email} refresh={refresh} />}
        <footer>Concurso interno Grupo Trafalgar · Desempate: más votos de 1º lugar, luego de 2º, luego el que se registró primero.</footer>
      </main>
      {showBallot && <Ballot entries={entries} ballot={ballot} dirty={dirty} saving={saving} onSave={saveVote} />}
      <Lightbox entry={lb} onClose={() => setLb(null)} />
      <Toaster position="bottom-center" offset={110} toastOptions={{ className: "ptl-toast" }} />
    </div>
  );
}
